// ======================================================================
// File: QAInspectionDrawer.jsx
// Module: PPE → Quality → Incoming QA
// Purpose: SAP-style inspection workstation drawer (FINAL TEST VERSION)
// ======================================================================

import React, { useState, useEffect } from "react";
import incomingQualityService from "../../services/incomingQualityService";
import { toast } from "react-toastify";

export default function QAInspectionDrawer({
  show,
  onClose,
  line,
  onSaved
}) {

  const [acceptedQty, setAcceptedQty] = useState(0);
  const [rejectedQty, setRejectedQty] = useState(0);
  const [parameters, setParameters] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // -----------------------------------------------------------
  // RESET WHEN LINE CHANGES
  // -----------------------------------------------------------
  useEffect(() => {
    if (line) {
      setAcceptedQty(line.accepted_qty || 0);
      setRejectedQty(line.rejected_qty || 0);
      setParameters(line.parameters || []);
      setError("");
    }
  }, [line]);

  if (!show || !line) return null;

  // -----------------------------------------------------------
  // LOCK CHECK (AUDIT SAFETY)
  // -----------------------------------------------------------
  const isLocked =
    line.status_code === "ACCEPTED" ||
    line.status_code === "REJECTED" ||
    line.status_code === "OVERRIDDEN";

  // -----------------------------------------------------------
  // QTY CALCULATION
  // -----------------------------------------------------------
  const receivedQty = Number(line.received_qty || 0);
  const accepted = Number(acceptedQty || 0);
  const rejected = Number(rejectedQty || 0);

  const pendingQty = receivedQty - accepted - rejected;

  const isQtyValid =
    accepted >= 0 &&
    rejected >= 0 &&
    pendingQty >= 0;

  // -----------------------------------------------------------
  // PARAMETER EVALUATION
  // -----------------------------------------------------------
  const evaluateParameter = (param) => {

    if (!param.observed_value) return "NA";

    // numeric range evaluation
    if (param.min_value != null && param.max_value != null) {
      const val = Number(param.observed_value);
      if (isNaN(val)) return "FAIL";

      return val >= param.min_value && val <= param.max_value
        ? "PASS"
        : "FAIL";
    }

    // pass/fail expected value
    if (param.expected_value) {
      return param.observed_value === param.expected_value
        ? "PASS"
        : "FAIL";
    }

    return "PASS";
  };

  const allParametersFilled =
    parameters.length > 0 &&
    parameters.every(
      p => p.observed_value !== "" && p.observed_value != null
    );

  // -----------------------------------------------------------
  // SAFE PARAMETER UPDATE (IMMUTABLE)
  // -----------------------------------------------------------
  const handleParameterChange = (index, value) => {
    setParameters(prev =>
      prev.map((p, i) =>
        i === index ? { ...p, observed_value: value } : p
      )
    );
  };

  // -----------------------------------------------------------
  // SAVE
  // -----------------------------------------------------------
  const handleSave = async () => {

    if (isLocked) {
      toast.warning("Inspection line is locked.");
      return;
    }

    if (!isQtyValid) {
      setError("Accepted + Rejected cannot exceed received quantity.");
      toast.warning("Invalid quantity entered.");
      return;
    }

    if (!allParametersFilled) {
      setError("All inspection parameters must be entered.");
      toast.warning("Please fill all parameters.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      await incomingQualityService.saveParameterReading({
        inspection_line_id: line.id,
        accepted_qty: accepted,
        rejected_qty: rejected,
        remarks: "",
        parameters: parameters.map(param => ({
          parameter_id: param.parameter_id,
          observed_value: param.observed_value
        }))
      });

      toast.success("Inspection readings saved");

      onSaved();
      onClose();

    } catch (err) {
      console.error(err);
      setError("Failed to save inspection.");
      toast.error("Failed to save inspection");
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------
  // UI
  // -----------------------------------------------------------
  return (
    <div style={{
      position: "fixed",
      right: 0,
      top: 0,
      width: 540,
      height: "100%",
      background: "#fff",
      boxShadow: "-4px 0 20px rgba(0,0,0,0.2)",
      zIndex: 9999,
      padding: 20,
      overflowY: "auto"
    }}>

      <h5>Inspect Material</h5>
      <hr />

      <div><b>Material:</b> {line.material_name}</div>
      <div><b>Batch:</b> {line.batch_no}</div>
      <div className="mb-3">
        <b>Received Qty:</b> {receivedQty}
      </div>

      {/* ================= PARAMETERS ================= */}
      <h6>Inspection Parameters</h6>

      <table className="table table-sm">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Specification</th>
            <th>Observed</th>
            <th>Result</th>
          </tr>
        </thead>

        <tbody>
          {parameters.map((p, idx) => {
            //const result = evaluateParameter(p);
            const result = p.result_code || "NA";

            return (
              <tr key={idx}>
                <td>{p.parameter_name}</td>
                <td>{p.specification}</td>

                <td>

                  {p.data_type === "PASS_FAIL" ? (

                    <select
                      className="form-select form-select-sm"
                      value={p.observed_value || ""}
                      disabled={isLocked}
                      onChange={(e) =>
                        handleParameterChange(idx, e.target.value)
                      }
                    >
                      <option value="">Select</option>
                      <option value="PASS">PASS</option>
                      <option value="FAIL">FAIL</option>
                    </select>

                  ) : (

                    <input
                      className="form-control form-control-sm"
                      value={p.observed_value || ""}
                      disabled={isLocked}
                      onChange={(e) =>
                        handleParameterChange(idx, e.target.value)
                      }
                    />

                  )}

                </td>

                <td>
                  {result === "PASS" && (
                    <span className="badge bg-success">PASS</span>
                  )}
                  {result === "FAIL" && (
                    <span className="badge bg-danger">FAIL</span>
                  )}
                  {result === "NA" && (
                    <span className="badge bg-secondary">NA</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ================= DECISION ================= */}
      <h6 className="mt-3">Decision</h6>

      <input
        type="number"
        min="0"
        className="form-control mb-2"
        placeholder="Accepted Qty"
        value={acceptedQty}
        disabled={isLocked}
        onChange={(e) => setAcceptedQty(e.target.value)}
      />

      <input
        type="number"
        min="0"
        className="form-control mb-2"
        placeholder="Rejected Qty"
        value={rejectedQty}
        disabled={isLocked}
        onChange={(e) => setRejectedQty(e.target.value)}
      />

      <div className={`fw-bold mb-3 ${isQtyValid ? "text-success" : "text-danger"}`}>
        Pending Qty : {pendingQty}
      </div>

      {error && (
        <div className="alert alert-danger py-2">{error}</div>
      )}

      {/* ================= ACTIONS ================= */}
      <div style={{ display: "flex", gap: 10 }}>
        <button
          className="btn btn-success"
          disabled={
            saving ||
            isLocked ||
            !isQtyValid ||
            !allParametersFilled
          }
          onClick={handleSave}
        >
          {saving ? "Saving..." : "Save Reading"}
        </button>

        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>

    </div>
  );
}